import React, {
  useRef, useContext, useEffect, useState, useCallback,
} from 'react';
import clsx from 'clsx';
import {
  Swiper, SwiperSlide, useSwiper, useSwiperSlide,
} from 'swiper/react';
import 'swiper/css';
import { debounce } from 'lodash';
import StreamContext from 'utils/StreamContext'
import usePosts from 'sdk/hooks/usePosts';
import Page from 'modules/app/components/Page';
import Post from 'modules/feed//components/Feed/ChartPreview';
import Comments from 'modules/feed/components/Feed/Comments';
import AssetDetails from 'modules/feed/components/Feed/AssetDetails';
import styles from 'modules/feed/components/Feed/Feed.module.scss';
import CategorySelector from 'modules/feed/components/Feed/CategorySelector';

function PostBlock(props: any) {
  const [commentsVisible, setCommentsVisible] = useState<boolean>(false)
  const [activeAsset, setActiveAsset] = useState<any>()
  const swiperSlide = useSwiperSlide();
  const swiper = useSwiper();

  useEffect(() => {
    if (!swiperSlide.isActive) {
      setCommentsVisible(false)
      setActiveAsset(null)
    } else {
      props?.onSetCurrentSlide(props?.idx)
    }
    if (swiperSlide.isActive && props?.idx === swiper.slides.length - 2) {
      props?.onNextPage()
    }
  }, [swiperSlide.isActive])

  const setPanelSize = (ref: HTMLDivElement) => {
    const panelInnerElement = ref
    if (panelInnerElement) {
      const containerElement = ref.parentElement?.parentElement;
      panelInnerElement.style.height = `${containerElement?.offsetHeight}px`;
      if (window.innerWidth <= 1400) {
        panelInnerElement.style.width = `${containerElement?.offsetWidth}px`;
      } else {
        panelInnerElement.style.width = '100%';
      }
    }
  }

  return (
    <div
      className={
        activeAsset || commentsVisible
          ? clsx(styles.panel, styles.openChatOrAssets)
          : styles.panel
      }
    >
      <div
        className={styles.panelInner}
        ref={(refPanelInner) => {
          if (refPanelInner) {
            setPanelSize(refPanelInner)

            window.addEventListener('resize', () => {
              setPanelSize(refPanelInner);
            })
          }
        }}
      >
        {activeAsset ? (
          <AssetDetails
            className={clsx(styles.panelSection, 'swiper-no-swiping')}
            activeAsset={activeAsset.id}
            setActiveAsset={setActiveAsset}
            onSaveCurrentState={props?.onSaveCurrentState}
          />
        ) : null}
        <Post
          {...props}
          hasCategories
          hasComment
          className={styles.panelSection}
          onShowComments={
            () => {
              setCommentsVisible(true)
              if (window.innerWidth <= 1400) {
                setActiveAsset(null)
              }
            }
          }
          activeAsset={activeAsset}
          setActiveAsset={(asset) => {
            setActiveAsset(asset);
            if (window.innerWidth <= 1400) {
              setCommentsVisible(false)
            }
          }}
        />
        {swiperSlide.isActive ? (
          <Comments
            {...props}
            className={clsx(
              styles.panelSection,
              'swiper-no-swiping',
              !commentsVisible && styles.panelSection_hide,
            )}
            onClose={() => setCommentsVisible(false)}
          />
        ) : null}
        <div
          className={styles.mobileModalBackground}
          onClick={() => {
            setCommentsVisible(false)
            setActiveAsset(null)
          }}
        />
      </div>
    </div>
  )
}

function Wheel({ wheelStatus }) {
  const swiper = useSwiper();
  useEffect(() => {
    if (wheelStatus === 'up') {
      swiper.slideNext();
    }
    if (wheelStatus === 'down') {
      swiper.slidePrev();
    }
  }, [wheelStatus])

  return null;
}

let subscription: any

function Feed() {
  const feedRef = useRef(null)
  const { client } = useContext(StreamContext)
  const [feed, setFeed] = useState<any>()
  const [feedGroup, setFeedGroup] = useState<any>('global')
  const feedName = 'global_flat'
  const prevDeltaY = useRef(0);
  const prev2DeltaY = useRef(0);
  const prev3DeltaY = useRef(0);
  const [videoMuted, setVideoMuted] = useState<boolean>(true);
  const [wheelStatus, setWheelStatus] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [wheelTesting, setWheelTesting] = useState<boolean>(false);
  const [wheelTouchPad, setWheelTouchPad] = useState<boolean>(false);

  useEffect(() => {
    if (client) {
      if ((feedGroup === 'global') || (feedGroup === 'following')) {
        setFeed(client.feed(feedName, feedGroup))
      } else {
        setFeed(client.feed(feedName, `cat-${feedGroup}`))
      }
    }
  }, [client, feedGroup])

  const feedData = usePosts(feed, '', 5, 'user_activity', client)

  useEffect(() => {
    if (feed) {
      subscription = feed
        .subscribe(() => {
          feedData.refetch()
        });
    }
    return () => {
      if (subscription?.cancel) subscription.cancel()
    }
  }, [feed])

  const posts = feedData?.data?.pages.map((v) => v?.results).flat() || null

  const handleNextPage = useCallback(() => {
    if (feedData.hasNextPage) {
      feedData.fetchNextPage()
    }
  }, [feedData.hasNextPage])

  const debouncedOnWheel = debounce((status) => {
    setWheelStatus(status);
    setTimeout(() => {
      setWheelStatus(null);
    }, 300)
  }, 310, {
    leading: true,
    trailing: false,
  })

  function detectTouchPad(e: any) {
    return e.wheelDeltaY ? e.wheelDeltaY === -3 * e.deltaY : e.deltaMode === 0
  }

  const OnTouchWheel = (deltaY: number) => {
    const deltaYRounded = deltaY;
    if (deltaYRounded > 0
      && deltaYRounded > prevDeltaY.current
      && prevDeltaY.current > prev2DeltaY.current
      && prev2DeltaY.current > prev3DeltaY.current) {
      debouncedOnWheel('up');
    }
    if (deltaYRounded < 0
      && deltaYRounded < prevDeltaY.current
      && prevDeltaY.current < prev2DeltaY.current
      && prev2DeltaY.current < prev3DeltaY.current) {
      debouncedOnWheel('down');
    }
    prev3DeltaY.current = prev2DeltaY.current;
    prev2DeltaY.current = prevDeltaY.current;
    prevDeltaY.current = deltaYRounded;
  }

  const OnMouseWheel = (deltaY: number) => {
    const deltaYRounded = deltaY;
    if (deltaYRounded > 0) {
      debouncedOnWheel('up');
    }
    if (deltaYRounded < 0) {
      debouncedOnWheel('down');
    }
  }

  const handleSaveCurrentState = () => {
    sessionStorage.setItem('feedData', JSON.stringify(feedData))
    sessionStorage.setItem('feedGroup', JSON.stringify(feedGroup))
    sessionStorage.setItem('feedCurrentIndex', currentSlide.toString())
  }

  /* autoplay testing */
  useEffect(() => {
    const autoplayTestContent = document.createElement('video');

    autoplayTestContent.src = 'data:video/mp4;base64,AAAAFGZ0eXBNU05WAAACAE1TTlYAAAOUbW9vdgAAAGxtdmhkAAAAAM9ghv7PYIb+AAACWAAACu8AAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAnh0cmFrAAAAXHRraGQAAAAHz2CG/s9ghv4AAAABAAAAAAAACu8AAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAFAAAAA4AAAAAAHgbWRpYQAAACBtZGhkAAAAAM9ghv7PYIb+AAALuAAANq8AAAAAAAAAIWhkbHIAAAAAbWhscnZpZGVBVlMgAAAAAAABAB4AAAABl21pbmYAAAAUdm1oZAAAAAAAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAVdzdGJsAAAAp3N0c2QAAAAAAAAAAQAAAJdhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAFAAOABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAAEmNvbHJuY2xjAAEAAQABAAAAL2F2Y0MBTUAz/+EAGGdNQDOadCk/LgIgAAADACAAAAMA0eMGVAEABGjuPIAAAAAYc3R0cwAAAAAAAAABAAAADgAAA+gAAAAUc3RzcwAAAAAAAAABAAAAAQAAABxzdHNjAAAAAAAAAAEAAAABAAAADgAAAAEAAABMc3RzegAAAAAAAAAAAAAADgAAAE8AAAAOAAAADQAAAA0AAAANAAAADQAAAA0AAAANAAAADQAAAA0AAAANAAAADQAAAA4AAAAOAAAAFHN0Y28AAAAAAAAAAQAAA7AAAAA0dXVpZFVTTVQh0k/Ou4hpXPrJx0AAAAAcTVREVAABABIAAAAKVcQAAAAAAAEAAAAAAAAAqHV1aWRVU01UIdJPzruIaVz6ycdAAAAAkE1URFQABAAMAAAAC1XEAAACHAAeAAAABBXHAAEAQQBWAFMAIABNAGUAZABpAGEAAAAqAAAAASoOAAEAZABlAHQAZQBjAHQAXwBhAHUAdABvAHAAbABhAHkAAAAyAAAAA1XEAAEAMgAwADAANQBtAGUALwAwADcALwAwADYAMAA2ACAAMwA6ADUAOgAwAAABA21kYXQAAAAYZ01AM5p0KT8uAiAAAAMAIAAAAwDR4wZUAAAABGjuPIAAAAAnZYiAIAAR//eBLT+oL1eA2Nlb/edvwWZflzEVLlhlXtJvSAEGRA3ZAAAACkGaAQCyJ/8AFBAAAAAJQZoCATP/AOmBAAAACUGaAwGz/wDpgAAAAAlBmgQCM/8A6YEAAAAJQZoFArP/AOmBAAAACUGaBgMz/wDpgQAAAAlBmgcDs/8A6YEAAAAJQZoIBDP/AOmAAAAACUGaCQSz/wDpgAAAAAlBmgoFM/8A6YEAAAAJQZoLBbP/AOmAAAAACkGaDAYyJ/8AFBAAAAAKQZoNBrIv/4cMeQ==';
    autoplayTestContent.id = 'base64_test_video';
    autoplayTestContent.autoplay = true;
    autoplayTestContent.playsInline = true;
    autoplayTestContent.style.position = 'fixed';
    autoplayTestContent.style.left = '5000px';
    autoplayTestContent.play()

    document.getElementsByTagName('body')[0].appendChild(autoplayTestContent);

    let iteration = 0;

    const interval = setInterval(() => {
      if (!autoplayTestContent?.paused && videoMuted && iteration) {
        setVideoMuted(false)
        clearInterval(interval)
        document.getElementsByTagName('body')[0].removeChild(autoplayTestContent);
      }
      iteration += 1
      if (iteration === 5) {
        clearInterval(interval)
        document.getElementsByTagName('body')[0].removeChild(autoplayTestContent);
      }
    }, 200)
  }, [])

  const handleMuted = (value) => {
    setVideoMuted(value)
  }

  const handleSetCurrentSlide = (slide) => {
    setCurrentSlide(slide)
  }

  return (
    <div
      className={styles.container}
      onWheel={(e: any) => {
        e.stopPropagation()
        if (!wheelTesting) {
          setWheelTesting(true)
          setWheelTouchPad(detectTouchPad)

          setTimeout(() => {
            setWheelTesting(false)
          }, 1000)
        }
        if (wheelTouchPad) {
          OnTouchWheel(e?.deltaY)
        } else {
          OnMouseWheel(e?.deltaY)
        }
      }}
    >
      <Swiper
        className={styles.posts}
        ref={feedRef}
        direction="vertical"
        slidesPerView={1}
        centeredSlides
        spaceBetween={0}
        breakpoints={{
          501: {
            slidesPerView: 'auto',
            spaceBetween: 10,
            autoHeight: true,
          },
          1401: {
            slidesPerView: 'auto',
            spaceBetween: 20,
            autoHeight: true,
          },
        }}
      >
        <Wheel wheelStatus={wheelStatus} />
        {posts?.length ? (posts.map((post: any, idx: number) => (
          <SwiperSlide key={post?.id} className={styles.postContainer}>
            {({ isActive, isNext, isPrev }) => (
              isActive || isNext || isPrev ? (
                <PostBlock
                  data={post}
                  feed={feed}
                  refresh={feedData?.refetch}
                  setCategoryFilter={setFeedGroup}
                  category={feedGroup}
                  videoMuted={videoMuted}
                  onVideoMuted={handleMuted}
                  onNextPage={handleNextPage}
                  onSaveCurrentState={handleSaveCurrentState}
                  onSetCurrentSlide={handleSetCurrentSlide}
                  idx={idx}
                />
              ) : (
                <div className={styles.panel} />
              )
            )}
          </SwiperSlide>
        ))) : null}
        {feedData.hasNextPage ? (
          <SwiperSlide className={styles.postContainer}>
            <div className={styles.panel}>
              <div className={styles.panelInner}>
                <div className={clsx(
                  styles.panelSection,
                  styles.panelSection_placeholder,
                )}
                >
                  <div className={styles.loader} />
                </div>
              </div>
            </div>
          </SwiperSlide>
        ) : null}
      </Swiper>
    </div>
  )
}

function PostList() {
  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    return () => {
      document.body.classList.remove('overflow-hidden');
    }
  })

  return (
    <Page activeLink="/feed">
      <Feed />
    </Page>
  )
}

export default PostList
